const SUPPORTED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'svg',
  'webp',
  'avif',
];

function readContentDispositionAsObject(
  contentDisposition: string
): Record<string, string | undefined> {
  return Object.fromEntries(
    contentDisposition.split(';').map((part: string): [string, string] => {
      const trimmed = part.trim();
      const tuple = trimmed.split('=');

      if (tuple.length === 1) {
        return [tuple[0], 'true'];
      }
      if (tuple.length === 2) {
        return [tuple[0], tuple[1]];
      }

      const [head, ...tail] = tuple;
      return [head, tail.join('=')];
    })
  );
}

function getExtensionFromPath(path: string): string | undefined {
  return path.includes('.') ? path.split('.').pop() : undefined;
}

/**
 * Checks if a given URL is an image URL. It also takes into consideration
 * URLs that do not have an extension but have a `response-content-disposition`
 * query parameter containing a filename with an extension.
 * @param url - The URL to check.
 * @returns A boolean indicating whether the URL is an image URL. Returns `null` if unable to determine.
 */
export function isImageUrl(url: string) {
  try {
    if (url.startsWith('data:') || url.startsWith('blob:')) return null;

    const relative = !url.startsWith('http:') && !url.startsWith('https:');

    const parsedUrl = new URL(`${relative ? 'http://a.a/' : ''}${url}`);
    const pathname = parsedUrl.pathname;

    let extension = getExtensionFromPath(pathname);
    if (extension) {
      return SUPPORTED_EXTENSIONS.includes(extension);
    }

    const contentDisposition = parsedUrl.searchParams.get(
      'response-content-disposition'
    );

    if (!contentDisposition) return null;

    const contentDispositionFilename =
      readContentDispositionAsObject(contentDisposition)?.filename;

    if (!contentDispositionFilename) return null;

    extension = getExtensionFromPath(contentDispositionFilename);
    if (extension) {
      return SUPPORTED_EXTENSIONS.includes(extension);
    }

    return null;
  } catch {
    return null;
  }
}
